import { useQuery } from '@tanstack/react-query';
import apiClient from './client';
import type { GraphData, GraphStats, Community } from '../types';

interface TraverseParams {
  start_node_id: string;
  depth?: number;
  relationship_types?: string[];
  min_confidence?: number;
  max_nodes?: number;
}

async function graphTraverse(params: TraverseParams): Promise<GraphData> {
  const { data } = await apiClient.post('/graph/traverse', params);
  return data;
}

async function shortestPath(sourceId: string, targetId: string): Promise<GraphData> {
  const { data } = await apiClient.post('/graph/shortest-path', {
    source_id: sourceId,
    target_id: targetId,
  });
  return data;
}

async function fetchCommunities(minSize: number = 3): Promise<Community[]> {
  const { data } = await apiClient.post('/graph/communities', {
    min_community_size: minSize,
  });
  return data.communities || data;
}

async function fetchGraphStats(): Promise<GraphStats> {
  try {
    const { data } = await apiClient.get('/graph/stats');
    return {
      total_nodes: data.total_nodes ?? 0,
      total_edges: data.total_edges ?? 0,
      density: data.density ?? 0,
      avg_degree: data.avg_degree ?? 0,
      communities: data.connected_components ?? 0,
      entity_type_counts: (data.node_types ?? {}) as Record<string, number>,
      relationship_type_counts: (data.edge_types ?? {}) as Record<string, number>,
    } as GraphStats;
  } catch {
    return { total_nodes: 0, total_edges: 0, density: 0, avg_degree: 0, communities: 0, entity_type_counts: {} as Record<string, number>, relationship_type_counts: {} as Record<string, number> } as GraphStats;
  }
}

async function fetchFullGraph(limit: number = 500): Promise<GraphData> {
  // Fetch entities + relationships from PostgreSQL to build graph data
  try {
    const [entitiesRes] = await Promise.all([
      apiClient.get('/entities', { params: { page_size: limit } }),
    ]);

    const entities = entitiesRes.data?.items || [];
    const nodes = entities.map((e: Record<string, unknown>) => ({
      id: String(e.id),
      label: String(e.name || ''),
      type: String(e.entity_type || e.type || 'document'),
      name: String(e.name || ''),
      risk_score: Number(e.risk_score || 0),
      confidence: Number(e.confidence || 1),
      properties: (e.properties || {}) as Record<string, unknown>,
    }));

    // Fetch relationships for each entity (batch)
    const edges: Array<{ id: string; source: string; target: string; type: string; label: string; confidence: number; weight: number }> = [];
    const seenEdges = new Set<string>();

    for (const node of nodes.slice(0, 50)) {
      try {
        const { data: rels } = await apiClient.get(`/entities/${node.id}/relationships`, {
          params: { limit: 20 },
        });
        for (const rel of (rels || [])) {
          const edgeId = String(rel.id);
          if (!seenEdges.has(edgeId)) {
            seenEdges.add(edgeId);
            edges.push({
              id: edgeId,
              source: String(rel.source_id),
              target: String(rel.target_id),
              type: String(rel.relationship_type || rel.type || 'related_to'),
              label: String(rel.relationship_type || rel.type || 'related_to'),
              confidence: Number(rel.confidence || 1),
              weight: 1,
            });
          }
        }
      } catch { /* skip */ }
    }

    return {
      nodes,
      edges,
      depth_reached: 1,
      total_nodes: nodes.length,
      total_edges: edges.length,
    } as GraphData;
  } catch {
    return { nodes: [], edges: [], depth_reached: 0, total_nodes: 0, total_edges: 0 } as GraphData;
  }
}

// ── React Query Hooks ──

export function useGraphTraverse(params: TraverseParams | null) {
  return useQuery({
    queryKey: ['graph-traverse', params],
    queryFn: () => graphTraverse(params!),
    enabled: !!params,
    staleTime: 60_000,
  });
}

export function useShortestPath(sourceId: string | null, targetId: string | null) {
  return useQuery({
    queryKey: ['shortest-path', sourceId, targetId],
    queryFn: () => shortestPath(sourceId!, targetId!),
    enabled: !!sourceId && !!targetId,
  });
}

export function useCommunities(minSize: number = 3) {
  return useQuery({
    queryKey: ['communities', minSize],
    queryFn: () => fetchCommunities(minSize),
    staleTime: 120_000,
  });
}

export function useGraphStats() {
  return useQuery({
    queryKey: ['graph-stats'],
    queryFn: fetchGraphStats,
    staleTime: 60_000,
  });
}

export function useFullGraph(limit: number = 500) {
  return useQuery({
    queryKey: ['full-graph', limit],
    queryFn: () => fetchFullGraph(limit),
    staleTime: 60_000,
  });
}
